import type {
  PatientRow,
  MedRecent,
  ProjectComplianceConfig,
  Triage,
  StreamCoverage
} from "./complianceTypes";
import { STREAM_DISPLAY, STREAM_ORDER, TRIAGE_ORDER } from "./complianceTypes";
import { dateDaysBefore, daysBetween, localDate } from "./complianceClock";

// Triage is recency only; medication adherence never affects it.
export interface TriageConfig {
  // One threshold for every stream, medication included (compliance_threshold_days).
  missingDays: number;
  // At most missingDays (at_risk_threshold_days).
  atRiskDays: number;
}

export const NEW_PATIENT_DAYS = 7;

export function triageConfigFor(
  config: Omit<ProjectComplianceConfig, "projectId">
): TriageConfig {
  return {
    missingDays: config.complianceThresholdDays,
    atRiskDays: config.atRiskThresholdDays
  };
}

export type TriageFilter = "everyone" | "attention" | "completed";

// Which medicines count towards medication compliance. Study (the project's list) and PD
// (classification tags) overlap rather than nest; "all" also counts medicines that are
// neither (eg ibuprofen).
export type MedBasis = "study" | "pd" | "all";

export type SortField = "triage" | "startDate" | "createdAt" | "lastOutreach";
export type SortDirection = "asc" | "desc";

// Matches the config's categories (carrotgraph ComplianceMetricCategory).
export type MissingDataStream =
  | "tremor_dyskinesia"
  | "mobility"
  | "sleep"
  | "medication"
  | "tap_test"
  | "bowel_movement";

// The null settings below mean "use the project's compliance configuration"; overrides last
// for the session.
export interface ComplianceFilters {
  triageFilter: TriageFilter;
  medBasis: MedBasis | null;
  // Selected data filter card (single-select).
  missingDataStream: MissingDataStream | null;
  triageConfig: TriageConfig | null;
  visibleStreams: MissingDataStream[] | null;
  sortField: SortField;
  sortDirection: SortDirection;
  createdWithinDays: number | null;
  outreachWithinDays: number | null;
  // Patient sidebar T&D hours line. Kept in memory per project like the other filters, so it's
  // lost on reload; display only, so not a project config override.
  tdThresholdHours: number | null;
}

export function defaultMedBasis(
  config: Omit<ProjectComplianceConfig, "projectId">
): MedBasis {
  return config.studyMedications.length > 0 ? "study" : "all";
}

export const DATE_RANGE_OPTIONS: { label: string; days: number }[] = [
  { label: "3d", days: 3 },
  { label: "7d", days: 7 },
  { label: "14d", days: 14 },
  { label: "30d", days: 30 }
];

export const DEFAULT_TD_THRESHOLD_HOURS = 6;

export const DEFAULT_FILTERS: ComplianceFilters = {
  triageFilter: "everyone",
  medBasis: null,
  missingDataStream: null,
  triageConfig: null,
  visibleStreams: null,
  sortField: "triage",
  sortDirection: "desc",
  createdWithinDays: null,
  outreachWithinDays: null,
  tdThresholdHours: null
};

const TRIAGE_FILTER_ACTIONS: Record<TriageFilter, Triage[] | null> = {
  everyone: null,
  attention: ["out_of_compliance", "at_risk"],
  completed: ["done", "dnf"]
};

export const TRIAGE_FILTER_LABELS: Record<TriageFilter, string> = {
  everyone: "All triage levels",
  attention: "Needs attention",
  completed: "Completed"
};

export const MED_BASIS_LABELS: Record<MedBasis, string> = {
  study: "Study meds",
  pd: "PD meds",
  all: "All meds"
};

export function patientToday(patient: PatientRow, now = new Date()): string {
  return localDate(now, patient.timezone);
}

export function patientDaysAgo(
  patient: PatientRow,
  days: number,
  now = new Date()
): string {
  return dateDaysBefore(patientToday(patient, now), days);
}

export function daysMonitored(patient: PatientRow, today = new Date()): number {
  return Math.max(
    0,
    daysBetween(patient.window.start, patientToday(patient, today))
  );
}

// Recency of streams (time series) and event logs alike.
type Recency = Pick<StreamCoverage, "daysSinceLastData">;

// No data ever (or no entry) counts from monitoring start, so a new patient isn't missing yet.
export function isDataMissing(
  r: Recency | undefined,
  thresholdDays: number,
  monitoredDays: number
): boolean {
  const days = r?.daysSinceLastData ?? null;
  if (days !== null) return days >= thresholdDays;
  return monitoredDays >= thresholdDays;
}

export function isMedMissing(
  mr: MedRecent,
  thresholdDays: number,
  monitoredDays: number
): boolean {
  if (mr.daysSinceLastData === null) return monitoredDays >= thresholdDays;
  return mr.daysSinceLastData >= thresholdDays;
}

// The project's configuration plus session overrides.
export interface ComplianceView {
  projectConfig: Omit<ProjectComplianceConfig, "projectId">;
  columns: MissingDataStream[]; // the checks triage runs, in display order
  medBasis: MedBasis;
  triageConfig: TriageConfig;
}

// Display order for columns and checks.
export function sortStreams(streams: MissingDataStream[]): MissingDataStream[] {
  return [...streams].sort(
    (a, b) => STREAM_ORDER.indexOf(a) - STREAM_ORDER.indexOf(b)
  );
}

export function resolveComplianceView(
  config: ProjectComplianceConfig,
  filters: Pick<
    ComplianceFilters,
    "visibleStreams" | "medBasis" | "triageConfig"
  >
): ComplianceView {
  const columns = filters.visibleStreams ?? sortStreams(config.categories);
  return {
    projectConfig: config,
    columns,
    medBasis: filters.medBasis ?? defaultMedBasis(config),
    triageConfig: filters.triageConfig ?? triageConfigFor(config)
  };
}

// Based on created date only, so it applies in every triage group.
export function isNewPatient(patient: PatientRow, today = new Date()): boolean {
  return patient.createdAt >= patientDaysAgo(patient, NEW_PATIENT_DAYS, today);
}

// Uses the triage checks so card counts match the triage reasons.
export function isFilterMatch(
  patient: PatientRow,
  stream: MissingDataStream,
  config: TriageConfig,
  today = new Date()
): boolean {
  return runComplianceCheck(patient, stream, config, today).status === "fail";
}

export function applyComplianceFilters(
  patients: PatientRow[],
  filters: ComplianceFilters,
  config: TriageConfig,
  today = new Date()
): PatientRow[] {
  let result = patients;

  const allowedActions = TRIAGE_FILTER_ACTIONS[filters.triageFilter];
  if (allowedActions !== null) {
    result = result.filter((p) => allowedActions.includes(p.triage));
  }

  const stream = filters.missingDataStream;
  if (stream !== null) {
    result = result.filter((p) => isFilterMatch(p, stream, config, today));
  }

  const createdWithin = filters.createdWithinDays;
  if (createdWithin !== null) {
    result = result.filter(
      (p) => p.createdAt >= patientDaysAgo(p, createdWithin, today)
    );
  }

  const outreachWithin = filters.outreachWithinDays;
  if (outreachWithin !== null) {
    result = result.filter(
      (p) =>
        p.lastOutreachDate !== null &&
        p.lastOutreachDate >= patientDaysAgo(p, outreachWithin, today)
    );
  }

  return result;
}

const ALL_STREAMS = STREAM_ORDER as MissingDataStream[];

export type ComplianceCheck =
  // `reason` replaces the default "Missing {label}" in the triage reason.
  | { status: "fail"; label: string; reason?: string }
  | { status: "warn"; notes: string[] } // close to failing
  | { status: "pass" }
  // Waiting for first data.
  | { status: "too_early"; label: string }
  | { status: "not_applicable"; note: string | null };

// Names the basis: a patient can have a schedule, just not for the selected medicines.
export function noScheduleReason(basis: MedBasis): string {
  if (basis === "study") return "No study medication schedule";
  if (basis === "pd") return "No PD medication schedule";
  return "No medication schedule";
}

// From the patient's exemption (any category, medication included) or a stream marked exempt.
export function isExempt(
  patient: PatientRow,
  stream: MissingDataStream
): boolean {
  if (patient.exemption?.streams.includes(stream)) return true;
  return patient.streams.some((s) => s.name === stream && s.exempt);
}

// Medication and bowel movements come from the PRO data; if it failed to load there's nothing
// to check, which is different from nothing having arrived.
export function isAwaitingPro(
  patient: PatientRow,
  stream: MissingDataStream
): boolean {
  return (
    !patient.proLoaded &&
    (stream === "medication" || stream === "bowel_movement")
  );
}

// Null means nothing ever arrived; the silence then runs from monitoring start.
function silenceNote(label: string, days: number | null): string {
  return days === null ? `No ${label} yet` : `${label} ${days}d ago`;
}

function checkMedication(
  mr: MedRecent,
  config: TriageConfig,
  monitored: number
): ComplianceCheck {
  const label = STREAM_DISPLAY.medication.short;
  // Flagged straight away: no schedule usually means it wasn't set up. Patients meant to have
  // none are exempted.
  if (!mr.hasSchedule) {
    return { status: "fail", label, reason: noScheduleReason(mr.basis) };
  }
  // Last logged dose only: autologs and adherence don't count.
  if (isMedMissing(mr, config.missingDays, monitored)) {
    return { status: "fail", label };
  }
  // Before too early, so silence past the at-risk days counts even for new patients.
  if (isMedMissing(mr, config.atRiskDays, monitored)) {
    return {
      status: "warn",
      notes: [silenceNote(label, mr.daysSinceLastData)]
    };
  }
  // Only a check with no data yet waits; recent data passes however new the patient is.
  if (mr.daysSinceLastData === null) return { status: "too_early", label };
  return { status: "pass" };
}

function checkRecency(
  r: Recency | undefined,
  label: string,
  config: TriageConfig,
  monitored: number
): ComplianceCheck {
  if (isDataMissing(r, config.missingDays, monitored)) {
    return { status: "fail", label };
  }
  if (isDataMissing(r, config.atRiskDays, monitored)) {
    return {
      status: "warn",
      notes: [silenceNote(label, r?.daysSinceLastData ?? null)]
    };
  }
  if ((r?.daysSinceLastData ?? null) === null) {
    return { status: "too_early", label };
  }
  return { status: "pass" };
}

// Recency for every check but medication, which has its own (`medRecent`): a stream's, or
// the latest bowel movement log's.
export function recencyFor(
  patient: PatientRow,
  stream: Exclude<MissingDataStream, "medication">
): Recency | undefined {
  if (stream === "bowel_movement") return patient.bowelMovementRecent;
  return patient.streams.find((s) => s.name === stream);
}

export function runComplianceCheck(
  patient: PatientRow,
  stream: MissingDataStream,
  config: TriageConfig,
  today = new Date()
): ComplianceCheck {
  // Ended windows are Completed / Do not follow up, so their cells show no status either.
  if (!patient.window.isActive || isExempt(patient, stream)) {
    return { status: "not_applicable", note: null };
  }
  if (isAwaitingPro(patient, stream)) {
    return { status: "not_applicable", note: null };
  }
  const monitored = daysMonitored(patient, today);
  if (stream === "medication") {
    return checkMedication(patient.medRecent, config, monitored);
  }
  return checkRecency(
    recencyFor(patient, stream),
    STREAM_DISPLAY[stream]?.short ?? stream,
    config,
    monitored
  );
}

// For ordering rows; null when there's no silence to measure (exempt or no schedule).
export function checkSilenceDays(
  patient: PatientRow,
  stream: MissingDataStream,
  today = new Date()
): number | null {
  if (isExempt(patient, stream)) return null;
  const monitored = daysMonitored(patient, today);
  if (isAwaitingPro(patient, stream)) return null;
  if (stream === "medication") {
    const mr = patient.medRecent;
    if (!mr.hasSchedule) return null;
    return mr.daysSinceLastData ?? monitored;
  }
  return recencyFor(patient, stream)?.daysSinceLastData ?? monitored;
}

export interface TriageResult {
  triage: Triage;
  // One line each in the list; empty when there's nothing to explain.
  triageReasons: string[];
  flaggedChecks: number;
  longestSilenceDays: number | null;
}

// ORs the checks, medication included: any failure puts the patient out of compliance.
// Medication reads `patient.medRecent`, so derive it for the selected basis first
// (medRecentFor).
export function computeTriage(
  patient: PatientRow,
  config: TriageConfig,
  enabledChecks: MissingDataStream[] = ALL_STREAMS,
  today = new Date()
): TriageResult {
  if (!patient.window.isActive) {
    const done = patient.contact.state !== "exhausted";
    return {
      triage: done ? "done" : "dnf",
      triageReasons: [done ? "Window ended" : "Outreach stopped"],
      flaggedChecks: 0,
      longestSilenceDays: null
    };
  }

  const checks = enabledChecks.map((stream) =>
    runComplianceCheck(patient, stream, config, today)
  );
  const silences = enabledChecks.flatMap((stream) => {
    const days = checkSilenceDays(patient, stream, today);
    return days === null ? [] : [days];
  });
  const longestSilenceDays = silences.length > 0 ? Math.max(...silences) : null;

  const missing: string[] = [];
  const failReasons: string[] = [];
  const warnings: string[][] = [];
  for (const c of checks) {
    if (c.status === "fail") {
      if (c.reason !== undefined) failReasons.push(c.reason);
      else missing.push(c.label);
    }
    if (c.status === "warn") warnings.push(c.notes);
  }

  const failedCount = missing.length + failReasons.length;
  if (failedCount > 0) {
    const parts = [
      ...failReasons,
      ...(missing.length > 0 ? [`Missing ${missing.join(", ")}`] : [])
    ];
    return {
      triage: "out_of_compliance",
      triageReasons: parts,
      flaggedChecks: failedCount,
      longestSilenceDays
    };
  }

  if (warnings.length > 0) {
    return {
      triage: "at_risk",
      triageReasons: warnings.flat(),
      flaggedChecks: warnings.length,
      longestSilenceDays
    };
  }

  // After the known failures and warnings, flag any patients with unavailable data.
  // This group includes 'Recently added' patients, so that all patients whose
  // data failed to load can be viewed together for ease of debugging.
  const unavailable = enabledChecks.filter(
    (stream) => !isExempt(patient, stream) && isAwaitingPro(patient, stream)
  );
  if (unavailable.length > 0) {
    const labels = unavailable.map(
      (stream) => STREAM_DISPLAY[stream]?.short ?? stream
    );
    return {
      triage: "data_unavailable",
      triageReasons: [`Couldn't load ${labels.join(", ")}`],
      flaggedChecks: 0,
      longestSilenceDays
    };
  }

  // Show before 'Steady' group to track patient onboarding
  const waiting = checks.flatMap((c) =>
    c.status === "too_early" ? [c.label] : []
  );
  if (waiting.length > 0) {
    const monitored = daysMonitored(patient, today);
    const started =
      monitored === 0
        ? "Monitoring started today"
        : `Monitoring started ${monitored}d ago`;
    return {
      triage: "unscoreable",
      triageReasons: [started, `No ${waiting.join(", ")} yet`],
      flaggedChecks: 0,
      longestSilenceDays
    };
  }

  if (checks.some((c) => c.status === "pass")) {
    return {
      triage: "steady",
      triageReasons: [],
      flaggedChecks: 0,
      longestSilenceDays
    };
  }

  // Every check was skipped. Triage only counts the checks a patient isn't exempt from, so
  // with none left there's nothing failing: steady, with the exemption as the reason.
  if (enabledChecks.some((stream) => isExempt(patient, stream))) {
    return {
      triage: "steady",
      triageReasons: [patient.exemption?.reason ?? "Exempt from all checks"],
      flaggedChecks: 0,
      longestSilenceDays
    };
  }
  if (checks.length > 0) {
    return {
      triage: "steady",
      triageReasons: ["Nothing to check yet"],
      flaggedChecks: 0,
      longestSilenceDays
    };
  }
  return {
    triage: "steady",
    triageReasons: ["No compliance checks selected"],
    flaggedChecks: 0,
    longestSilenceDays
  };
}

// Worst first within a group: most flagged checks, then longest silence.
export function sortPatientsByTriage(patients: PatientRow[]): PatientRow[] {
  return [...patients].sort((a, b) => {
    const triageDiff =
      TRIAGE_ORDER.indexOf(a.triage) - TRIAGE_ORDER.indexOf(b.triage);
    if (triageDiff !== 0) return triageDiff;

    if (a.flaggedChecks !== b.flaggedChecks) {
      return b.flaggedChecks - a.flaggedChecks;
    }

    const silenceA = a.longestSilenceDays ?? -1;
    const silenceB = b.longestSilenceDays ?? -1;
    if (silenceA !== silenceB) return silenceB - silenceA;

    return a.codeName.localeCompare(b.codeName);
  });
}

export function sortPatients(
  patients: PatientRow[],
  field: SortField,
  direction: SortDirection
): PatientRow[] {
  // Triage has a canonical semantic order (worst first); direction is intentionally ignored.
  if (field === "triage") return sortPatientsByTriage(patients);
  const dir = direction === "asc" ? 1 : -1;
  return [...patients].sort((a, b) => {
    if (field === "startDate") {
      return dir * a.window.start.localeCompare(b.window.start);
    }
    if (field === "createdAt") {
      return dir * a.createdAt.localeCompare(b.createdAt);
    }
    const aDate = a.lastOutreachDate ?? "";
    const bDate = b.lastOutreachDate ?? "";
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return dir * aDate.localeCompare(bDate);
  });
}
